import {
  Avatar,
  Button,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  Pagination,
  RowStyles,
  ScrollableContent,
  SimpleHeaderAndData,
  column,
  dateColumn,
  selectColumn,
  simpleDataRows,
  simpleHeader,
  treeFilter,
  useComputed,
  useGridTableApi,
  usePersistedFilter,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import {
  addEntityParam,
  createProductOfferingConfigEditUrl,
  createProductOfferingConfigUrl,
  createProductOfferingConfigsCompareUrl,
  createProductOfferingUrl,
  createProductOfferingsUrl,
} from "src/RouteUrls";
import { QueryTable, SearchBox, dateCell } from "src/components";
import { Order, ProductOfferingConfigOrder, ProductOfferingConfigsFilter } from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlanAndOptionImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import {
  MarketProductOfferingConfigsPageMetadataFragment,
  ProductOfferingConfigDetailFragment,
  useProductOfferingConfigsPageMetadataQuery,
  useProductOfferingConfigsQuery,
} from "src/routes/libraries/product-offering-configs/ProductOfferingConfigsPage.generated";
import { pageSchema, queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";

export function ProductOfferingConfigsPage() {
  const query = useProductOfferingConfigsPageMetadataQuery();
  return queryResult(query, { data: ({ markets }) => <ProductOfferingConfigsPageView marketsOrIds={markets} /> });
}

export type ProductOfferingConfigsPageViewProps = {
  // Configures the view to show all markets and developments, a specific development, a specific project, or a specific product offering.
  marketsOrIds:
    | MarketProductOfferingConfigsPageMetadataFragment[]
    | { developmentId?: string; projectId?: string; productOfferingId?: string; rpavId?: string };
};

export function ProductOfferingConfigsPageView({ marketsOrIds }: ProductOfferingConfigsPageViewProps) {
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const [order, setOrder] = useState<ProductOfferingConfigOrder>({ updatedAt: Order.Desc });
  const filterDefs: FilterDefs<ProductOfferingConfigsFilter> = useMemo(
    () =>
      Array.isArray(marketsOrIds)
        ? {
            developments: treeFilter({
              defaultCollapsed: true,
              label: "Markets and Developments",
              filterBy: "leaf",
              getOptionLabel: (o) => o.name,
              getOptionValue: (o) => o.id,
              options: marketsOrIds.map((m) => ({
                id: m.id,
                name: m.name,
                children: m.developments.map((d) => ({ id: d.id, name: d.name })),
              })),
            }),
          }
        : {},
    [marketsOrIds],
  );

  const { filter, setFilter } = usePersistedFilter<ProductOfferingConfigsFilter>({
    storageKey: "productOfferingConfigsFilter",
    filterDefs,
  });
  const [searchFilter, setSearchFilter] = useState<string>("");

  const {
    developmentId,
    projectId,
    productOfferingId,
    rpavId: rpavIdFromProps,
  } = !Array.isArray(marketsOrIds)
    ? marketsOrIds
    : { developmentId: undefined, projectId: undefined, productOfferingId: undefined, rpavId: undefined };
  const query = useProductOfferingConfigsQuery({
    variables: {
      filter: {
        search: searchFilter || undefined,
        projects: projectId ? [projectId] : undefined,
        developments: developmentId ? [developmentId] : undefined,
        productOfferings: productOfferingId ? [productOfferingId] : undefined,
        ...filter,
      },
      order,
      page: pageSettings,
    },
  });
  const { loading, data } = query;
  const configs = data?.productOfferingConfigs.entities;
  const rpavId = rpavIdFromProps ?? rpavIdFromConfig(configs?.[0]);
  const backUrl = productOfferingId
    ? rpavId
      ? createProductOfferingUrl(productOfferingId, rpavId, developmentId, projectId)
      : createProductOfferingsUrl(developmentId, projectId)
    : createProductOfferingsUrl(developmentId, projectId);
  const subHeader = (
    <div>
      <div css={Css.xl2.$}>Compare Configurations</div>
      <div css={Css.gray700.$}>
        {productOfferingId
          ? "Compare the configurations created on the offering, or create new ones!"
          : "Select as many saved configurations on product offerings below, then select compare"}
      </div>
    </div>
  );

  const api = useGridTableApi<Row>();
  const selectedRows = useComputed(() => api.getSelectedRows(), [api]);
  const columns = useMemo(() => createColumns(!!(developmentId || projectId)), [developmentId, projectId]);
  const rowStyles: RowStyles<Row> = useMemo(
    () => ({
      data: {
        rowLink: ({ id, data: { productOffering } }) =>
          createProductOfferingConfigEditUrl({
            productOfferingConfigId: id,
            // Update url params with metaIds to load the page with selectable options & delta costs
            productOfferingId: productOffering.id,
            developmentId,
            projectId,
          }),
      },
    }),
    [developmentId],
  );
  const disableCompare = useMemo(
    () =>
      (selectedRows.isEmpty && "No configurations selected") ||
      (selectedRows.length > 10 && "No greater than 10 configurations can be compared at once"),
    [selectedRows.isEmpty, selectedRows.length],
  );

  return (
    <>
      {!loading && productOfferingId && configs?.isEmpty ? (
        <>
          <PageHeader title="Compare Configurations" hideTitle backButton={backUrl} left={subHeader} />
          <EmptyState
            message="There are no configurations on this offering yet"
            svg={<EmptyPlanAndOptionImage />}
            button={
              <Button
                label="Create Configuration"
                onClick={createProductOfferingConfigUrl({
                  idOrAdd: addEntityParam,
                  productOfferingId: productOfferingId,
                  developmentId,
                  projectId,
                })}
              />
            }
          />
        </>
      ) : (
        <>
          <PageHeader
            title="Compare Configurations"
            hideTitle
            backButton={backUrl}
            left={subHeader}
            right={
              <>
                {productOfferingId && (
                  <Button
                    label="Create New"
                    variant="secondary"
                    onClick={createProductOfferingConfigUrl({
                      idOrAdd: addEntityParam,
                      productOfferingId: productOfferingId,
                      developmentId,
                      projectId,
                    })}
                  />
                )}
                <Button
                  label="Compare"
                  variant="primary"
                  disabled={disableCompare}
                  onClick={createProductOfferingConfigsCompareUrl(
                    selectedRows.map((r) => r.id),
                    productOfferingId,
                    developmentId,
                    projectId,
                  )}
                />
              </>
            }
          />
          <TableActions>
            {!(developmentId || projectId) && <Filters filterDefs={filterDefs} filter={filter} onChange={setFilter} />}
            <SearchBox onSearch={setSearchFilter} />
          </TableActions>
          <ScrollableContent>
            <QueryTable
              {...{ api, columns, rowStyles, query }}
              createRows={() => [simpleHeader, ...simpleDataRows(data?.productOfferingConfigs.entities)]}
              sorting={{
                on: "server",
                onSort: (key, direction) => setOrder(toOrder(key, direction)),
                value: parseOrder(order),
              }}
              stickyHeader
              fallbackMessage="There aren't any configurations to display."
            />
            <Pagination
              page={[pageSettings, setPageSettings]}
              totalCount={data?.productOfferingConfigs.pageInfo.totalCount || 0}
            />
          </ScrollableContent>
        </>
      )}
    </>
  );
}

type Row = SimpleHeaderAndData<ProductOfferingConfigDetailFragment>;

function rpavIdFromConfig(config?: ProductOfferingConfigDetailFragment): string | undefined {
  const templates = config?.productOffering?.itemTemplates;
  return templates?.find((t) => t.isLatestActive)?.id ?? templates?.[0]?.id;
}

const createColumns = (hideDevelopment: boolean): GridColumn<Row>[] => [
  selectColumn(),
  column<Row>({ header: "Name", data: ({ name }) => name }),
  column<Row>({ header: "Offering", data: ({ productOffering }) => productOffering.name }),
  column<Row>({
    header: "Created by",
    data: ({ createdBy }) => (
      <Avatar src={createdBy?.internalUser?.avatar} name={createdBy.internalUser?.name} showName />
    ),
  }),
  dateColumn<Row>({ header: "Created", data: ({ createdAt }) => dateCell(createdAt) }),
  dateColumn<Row>({ header: "Updated", serverSideSortKey: "updatedAt", data: ({ updatedAt }) => dateCell(updatedAt) }),
  ...(hideDevelopment
    ? []
    : [column<Row>({ header: "Developments", data: ({ productOffering }) => productOffering.development?.name })]),
];
